/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2019 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

import React from "react";
import ReactDOM from "react-dom";
import { useStrict } from "mobx";
import {
    logging, addProvider, envUtil,
} from "dc-core";
import ServiceRegistry from "bundle/util/ServiceRegistry";
import UIActionConfigStore from "bundle/store/UIActionConfigStore";
import IntegrationProvider from "bundle/provider/IntegrationProvider";
import AppStore from "app/store/AppStore";
import LoggingService from "app/preview/service/LoggingService";
import LocaleService from "app/preview/service/LocaleService";
import AnalyticsProviderService from "app/preview/service/AnalyticsProviderService";
import PollingService from "bundle/service/PollingService";
import EventsService from "app/preview/service/EventsService";
import EmbedModeService from "app/preview/service/EmbedModeService";
import LicensingService from "app/preview/service/LicensingService";
import FullScreenManager from "app/preview/service/FullScreenManager";
import CommandReceiverService from "app/preview/service/CommandReceiverService";
import PerformanceService from "app/preview/service/PerformanceService";
import AdobeDCViewApp from "app/preview/react-component/AdobeDCViewApp";
import UIHandler from "app/preview/service/UIHandler";
import HUDService from "app/preview/service/HUDService";
import UserApi from "bundle/api/UserApi";
import FileApi from "app/api/FileApi";
import CommentsApi from "bundle/api/CommentsApi";
import DialogApi from "app/preview/react-component/dialogs";
import EditApi from "bundle/api/EditApi";
import DropinRegisteredApis from "bundle/api/DropinRegisteredApis";
import ConfigStore from "app/store/ConfigStore";
import ComponentRegistry from "bundle/util/ComponentRegistry";
import AppContainer from "app/preview/react-component/main-app/container/AppContainer";
import App from "app/preview/react-component/main-app/presentation/MainApp";
import Preview from "app/preview/react-component/preview/presentation/Preview";
import HeaderView from "bundle/preview/react-component/header/presentation/HeaderView";
import HeaderFileInfoView from "bundle/preview/react-component/header-file-info/presentation/HeaderFileInfoView";
import AdobeMenuActions from "app/preview/react-component/header/presentation/side-menu/AdobeMenuActions";
import ContinuousIcon from "icons/ContinuousIcon";
import SinglePageIcon from "icons/SinglePageIcon";
import PreviewErrorIcon from "icons/PreviewErrorIcon";
import AdobeDCIcon from "icons/AdobeDCIcon";
import HidingBrandingContainer from "bundle/preview/react-component/hiding-branding/container/HidingBrandingContainer";

useStrict(true);

const _registerComponents = () => {
    ComponentRegistry.register("AppContainer", AppContainer);
    ComponentRegistry.register("App", App);
    ComponentRegistry.register("Preview", Preview);
    ComponentRegistry.register("HeaderView", HeaderView);
    ComponentRegistry.register("AdobeMenuActions", AdobeMenuActions);
    ComponentRegistry.register("HeaderFileInfoView", HeaderFileInfoView);
    ComponentRegistry.register("ContinuousIcon", ContinuousIcon);
    ComponentRegistry.register("SinglePageIcon", SinglePageIcon);
    ComponentRegistry.register("HidingBrandingContainer", HidingBrandingContainer);
    ComponentRegistry.register("PreviewErrorIcon", PreviewErrorIcon);
    ComponentRegistry.register("AdobeDCIcon", AdobeDCIcon);
};

const _registerServices = () => {
    ServiceRegistry.register("ConfigStore", new ConfigStore());
    ServiceRegistry.register("UIActionConfigStore", new UIActionConfigStore());
    ServiceRegistry.register("AppStore", new AppStore());
    ServiceRegistry.register("UserApi", new UserApi());
    ServiceRegistry.register("FileApi", new FileApi());
    ServiceRegistry.register("CommentsApi", new CommentsApi());
    ServiceRegistry.register("DialogApi", new DialogApi());
    ServiceRegistry.register("EditApi", new EditApi());
    ServiceRegistry.register("DropinRegisteredApis", new DropinRegisteredApis());
    ServiceRegistry.register("IntegrationProvider", new IntegrationProvider());
    ServiceRegistry.register("LoggingService", new LoggingService());
    ServiceRegistry.register("EventsService", new EventsService());
    ServiceRegistry.register("PollingService", new PollingService());
    ServiceRegistry.register("LicensingService", new LicensingService());
    ServiceRegistry.register("CommandReceiverService", new CommandReceiverService());
    ServiceRegistry.register("FullScreenManager", new FullScreenManager());
    ServiceRegistry.register("PerformanceService", new PerformanceService());
    ServiceRegistry.register("LocaleService", new LocaleService());
    ServiceRegistry.register("AnalyticsProviderService", new AnalyticsProviderService());
    ServiceRegistry.register("EmbedModeService", new EmbedModeService());
    ServiceRegistry.getService("LicensingService").initialize();
    ServiceRegistry.getService("CommandReceiverService").initialize();
    ServiceRegistry.register("UIHandler", new UIHandler());
    ServiceRegistry.register("HUDService", new HUDService());
};

const _executeAction = (action, config, appStore) => {
    let waitForLogin;
    if (appStore.isInPreview) {
        logging.getLogger("App").info("Starting file preview for clientId:", config.clientId, config.eventOrigin);
        waitForLogin = Promise.resolve(true);
    }

    waitForLogin.then(() => {
        ServiceRegistry.getService("LoggingService").logExternal("Attaching app to dom.");
        ReactDOM.render(<AdobeDCViewApp store={ appStore } />, document.getElementById("adobe-dc-view"));
    });
};

_registerServices();
_registerComponents();
addProvider("integrationProvider", ServiceRegistry.getService("IntegrationProvider"));

window.renderApp = function (data) { // eslint-disable-line func-names
    ServiceRegistry.initServices(data.services);
    ServiceRegistry.getService("LoggingService").initialize(data.config);
    // TODO:: investigate why it is breaking local env
    envUtil.setEnvVars({ server_env: data.config.serverEnv, app_identifier: "integration" });
    const appStore = ServiceRegistry.getService("AppStore");
    appStore.initialize(data);
    ServiceRegistry.getService("IntegrationProvider").setAppStore(appStore);
    ServiceRegistry.getService("AnalyticsProviderService").initialize();
    ServiceRegistry.getService("PollingService").initialize();
    ServiceRegistry.getService("PerformanceService").initialize(data.action, data.timeInfo);
    ServiceRegistry.getService("LocaleService").initialize(window.userLocale);

    _executeAction(data.action, data.config, appStore);
};

// This will happen before service initialize and its fine.
ServiceRegistry.getService("PerformanceService").setAppReadyToRender();
